/* ---------------------------------------------------------------------------
 * extractPalette(items, opts)
 *
 * Compose-aware palette extraction: pulls a 5-colour cohesive palette from
 * an array of canvas items by sampling pixels from each item's image,
 * quantising to a small bucket space, weighting by visual area on the
 * canvas, and ordering the result by lightness so it reads as a strip.
 *
 * Returns: Promise<string[]> of 5 hex colours, e.g. ["#2B2620", …, "#F4ECDF"].
 *
 * Implementation notes:
 *  - Uses a hidden canvas to draw each image at ~64px wide, then samples
 *    every 4th pixel (~256 samples per image). Plenty for moodboards.
 *  - Quantises to a 5-bit-per-channel space (32×32×32 buckets) which gives
 *    crisp groupings without losing nuance.
 *  - Drops near-white and near-black extremes by default (visually they
 *    rarely belong in a "palette") unless the entire composition is very
 *    dark or very light.
 *  - All images are loaded via the /api/images/proxy endpoint when they're
 *    cross-origin, so we never get a tainted-canvas SecurityError.
 * ------------------------------------------------------------------------- */

import { API } from "@/lib/api";

const SAMPLE_WIDTH = 64;
const QUANT_BITS = 5; // 32 buckets / channel
const QUANT = 1 << (8 - QUANT_BITS); // 8

function toHex(r, g, b) {
  const h = (v) => v.toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`.toUpperCase();
}

function lightness(r, g, b) {
  return (Math.max(r, g, b) + Math.min(r, g, b)) / 2;
}

function loadImage(url) {
  // Route remote URLs through the backend proxy so the canvas can read pixels.
  const looksRemote = /^https?:\/\//i.test(url);
  const fullUrl = looksRemote
    ? `${API}/images/proxy?url=${encodeURIComponent(url)}`
    : url.startsWith("/")
      ? `${process.env.REACT_APP_BACKEND_URL || ""}${url}`
      : url;
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("img load failed"));
    img.src = fullUrl;
  });
}

function sampleImage(img, weight = 1) {
  // weight = visual area on canvas (drives how much this image "counts" in the
  // tally). Wider items get proportionally more sampling weight.
  const aspect = img.naturalHeight / Math.max(1, img.naturalWidth);
  const w = SAMPLE_WIDTH;
  const h = Math.max(8, Math.round(w * aspect));
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);
  const tally = new Map();
  for (let i = 0; i < data.length; i += 16) { // sample every 4th pixel (i+=4×4)
    const a = data[i + 3];
    if (a < 200) continue; // skip transparent
    // Quantise to a coarser bucket
    const r = Math.round(data[i]     / QUANT) * QUANT;
    const g = Math.round(data[i + 1] / QUANT) * QUANT;
    const b = Math.round(data[i + 2] / QUANT) * QUANT;
    const key = `${r},${g},${b}`;
    tally.set(key, (tally.get(key) || 0) + weight);
  }
  return tally;
}

function isNearGreyExtreme(r, g, b) {
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const sat = max === 0 ? 0 : (max - min) / max;
  return sat < 0.06 && (max < 30 || min > 232);
}

export async function extractPalette(items, opts = {}) {
  const wantCount = opts.count || 5;
  if (!Array.isArray(items) || items.length === 0) return [];
  const merged = new Map();

  // Limit to 6 most-visible items so a busy canvas doesn't take forever.
  const visible = items
    .slice()
    .sort((a, b) => (b.width * b.height) - (a.width * a.height))
    .slice(0, 6);

  for (const it of visible) {
    try {
      const img = await loadImage(it.image_url);
      const weight = Math.max(1, Math.round((it.width * it.height) / 8000));
      const tally = sampleImage(img, weight);
      for (const [k, v] of tally) {
        merged.set(k, (merged.get(k) || 0) + v);
      }
    } catch {
      // skip — couldn't load this image, just continue
    }
  }

  if (merged.size === 0) return [];

  // Rank, then de-cluster (don't take two buckets within ~32 RGB units)
  const sorted = Array.from(merged.entries())
    .map(([k, count]) => {
      const [r, g, b] = k.split(",").map(Number);
      return { r, g, b, count };
    })
    .sort((a, b) => b.count - a.count);

  // Drop the very-near-grey extremes unless the only candidates are extremes
  const filtered = sorted.filter(p => !isNearGreyExtreme(p.r, p.g, p.b));
  const pool = filtered.length >= wantCount ? filtered : sorted;

  const picked = [];
  const minDist = 56; // squared-distance threshold for "too similar"
  for (const p of pool) {
    const tooClose = picked.some(q => {
      const dr = p.r - q.r, dg = p.g - q.g, db = p.b - q.b;
      return (dr * dr + dg * dg + db * db) < (minDist * minDist);
    });
    if (!tooClose) picked.push(p);
    if (picked.length >= wantCount) break;
  }

  // Order by lightness so it reads as a graceful strip (dark → light)
  picked.sort((a, b) => lightness(a.r, a.g, a.b) - lightness(b.r, b.g, b.b));
  return picked.map(p => toHex(p.r, p.g, p.b));
}
